import axios from 'axios'
import wrappers from '../utils/wrappers'
import type { AuthSessionInfo, ImpersonationAccessTokenResponse } from '@/types/impersonation'
import type { QueueSnapshot } from '@/types/queueSnapshot'

const url = `${import.meta.env.VITE_API_URL}/v1/auth`

export default {
  guest() {
    return axios
      .post(`${url}/guest`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
  },

  register(params: { name: string; email: string; password: string; turnstileToken?: string }) {
    return axios
      .post(`${url}/register`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => wrappers.handleEscribonautaError(error, 'No se pudo crear el usuario'))
  },

  login(params: { email: string; password: string; restoreAccount?: boolean }) {
    return axios
      .post(`${url}/login`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => {
        const errorCode =
          error?.response?.data?.errorCode || error?.response?.data?.data?.errorCode
        if (errorCode === 'ACCOUNT_PENDING_DELETION') {
          return Promise.reject(error)
        }
        return wrappers.handleEscribonautaError(error, 'No se pudo iniciar la sesión')
      })
  },

  requestAccountDeletion(params: { reason: string }) {
    return axios
      .post(`${url}/account-deletion/request`, params, { timeout: 15000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo solicitar la eliminación de la cuenta'),
      )
  },

  logout() {
    return axios
      .post(`${url}/logout`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => Promise.reject(error))
  },

  initiatePasswordRecovery(params: { email: string }) {
    return axios
      .post(`${url}/password-recovery/initiate`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo iniciar la recuperación de contraseña'),
      )
  },

  verifyPasswordRecoveryToken(params: { token: string; password: string }) {
    return axios
      .post(`${url}/password-recovery/verify`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(
          error,
          'No se pudo verificar el token de recuperación de contraseña',
        ),
      )
  },

  sendEmailVerification() {
    return axios
      .post(`${url}/email-verification/send`, {}, { timeout: 15000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => {
        const status = error?.response?.status || error?.response?.data?.code
        const message =
          status === 429
            ? 'Espera unos segundos antes de pedir otro correo'
            : 'No se pudo enviar el correo de confirmación'
        return wrappers.handleEscribonautaError(error, message)
      })
  },

  verifyEmail(params: { token: string }) {
    return axios
      .post(`${url}/email-verification/verify`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => Promise.reject(error))
  },

  cancelEmailChange() {
    return axios
      .post(`${url}/email-change/cancel`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cancelar el cambio de correo'),
      )
  },

  patch(params: { name?: string; email?: string; password?: string; currentPassword?: string }) {
    return axios
      .patch(`${url}/`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => wrappers.handleEscribonautaError(error, 'No se pudo editar el perfil'))
  },

  patchPublicAlias(params: { publicAlias: string }) {
    return axios
      .patch(`${url}/public-alias`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo guardar tu alias público'),
      )
  },

  lookupPublicAliases(params: { query: string }) {
    return axios
      .get(`${url}/public-aliases`, { params, timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudieron buscar alias'),
      )
  },

  patchDonationPrompt(params: { lastDonationPromptAt: number }) {
    return axios
      .patch(`${url}/donation-prompt`, params, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => Promise.reject(error))
  },

  feedback(params: {
    type: 'bug' | 'suggestion'
    message: string
    routePath: string
    routeName?: string
    routeQuery?: Record<string, unknown>
    contactName?: string
    contactEmail?: string
  }) {
    return axios
      .post(`${url}/feedback`, params, { timeout: 15000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => {
        const status = error?.response?.status || error?.response?.data?.code
        const message =
          status === 429
            ? 'Has enviado demasiados reportes. Inténtalo más tarde.'
            : 'No se pudo enviar el mensaje'
        return wrappers.handleEscribonautaError(error, message)
      })
  },

  queueSnapshot() {
    return axios
      .get(`${url}/queue-snapshot`, {
        timeout: 10000,
        action: 'auth.queueSnapshot',
      })
      .then((response: any) => (
        wrappers.handleEscribonautaResponse(response) as { data: QueueSnapshot }
      ))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cargar la cola de correos de inactividad'),
      )
  },

  me() {
    return axios
      .get(`${url}/me`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: AuthSessionInfo })
  },

  impersonate(params: {
    targetUserId: string
    secret: string
  }) {
    return axios
      .post(`${url}/impersonate`, params, { timeout: 10000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: ImpersonationAccessTokenResponse })
      .catch((error: any) => wrappers.handleEscribonautaError(error, 'No se pudo impersonar al usuario'))
  },
}
